import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LifeBuoy, Loader2, Save, ChevronDown, ChevronUp, X, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import { TokenChips, insertAtCursor, ReceiptCouponPreview } from './copyTokens'
import type { APIResponse, RecoveryCouponConfig, RecoveryCouponTier } from '@/types'

/**
 * Recovery coupon — manager-issued, PIN-gated comeback code for service
 * recovery. When a sitting customer has a bad moment, a manager mints a
 * bigger-than-standard comeback code for their NEXT visit (it does NOT touch
 * the current bill). Configured here; issued from the counter rail.
 *
 * Stored as one composite JSON blob under the `recovery_coupon_config`
 * app_settings key. Percent-only by design — operators reason about severity
 * as "minor 15% / major 50%", and a single override cap keeps the guardrail
 * simple. Mirrors backend handlers.RecoveryCouponConfig.
 */

const SETTINGS_KEY = 'recovery_coupon_config'

// Kept in lock-step with marketing_promo_thank_you.go
// recoveryReceiptDefaultMessage. The receipt renderer converts \n into
// <br> and appends a "→" arrow pointing at the code box, so the saved
// message should NOT include the code or a trailing arrow.
const DEFAULT_MESSAGE = "Sorry today fell short.\n\nHere's {{discount}} off your next visit — on us."

const DEFAULTS: RecoveryCouponConfig = {
  enabled: false,
  tiers: [
    { key: 'minor', label: 'Minor', percent: 15, validity_days: 30 },
    { key: 'moderate', label: 'Moderate', percent: 30, validity_days: 30 },
    { key: 'major', label: 'Major', percent: 50, validity_days: 45 },
  ],
  override_cap: 80,
  reasons: ['Long wait', 'Order error', 'Food quality', 'Staff service', 'Manager discretion'],
  receipt_message: DEFAULT_MESSAGE,
}

function parseConfig(raw: unknown): RecoveryCouponConfig {
  if (!raw || typeof raw !== 'object') return structuredClone(DEFAULTS)
  const r = raw as Record<string, unknown>
  const tiers: RecoveryCouponTier[] = Array.isArray(r.tiers)
    ? (r.tiers as unknown[])
        .map((t) => {
          const o = (t ?? {}) as Record<string, unknown>
          const percent = typeof o.percent === 'number' ? o.percent : 0
          return {
            key: typeof o.key === 'string' ? o.key : '',
            label: typeof o.label === 'string' ? o.label : '',
            percent: Math.min(100, Math.max(0, percent)),
            validity_days:
              typeof o.validity_days === 'number' && o.validity_days > 0
                ? Math.round(o.validity_days)
                : 30,
          }
        })
        .filter((t) => t.key !== '' && t.percent > 0)
    : []
  return {
    enabled: r.enabled === true,
    tiers: tiers.length > 0 ? tiers : structuredClone(DEFAULTS.tiers),
    override_cap:
      typeof r.override_cap === 'number' && r.override_cap > 0
        ? Math.min(100, r.override_cap)
        : DEFAULTS.override_cap,
    reasons: Array.isArray(r.reasons)
      ? (r.reasons as unknown[]).filter((x): x is string => typeof x === 'string' && x.trim() !== '')
      : structuredClone(DEFAULTS.reasons),
    receipt_message:
      typeof r.receipt_message === 'string' && r.receipt_message.trim() !== ''
        ? r.receipt_message
        : DEFAULT_MESSAGE,
  }
}

export function AdminRecoveryCouponSettings() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const settingsQuery = useQuery<APIResponse<Record<string, unknown>>>({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 60_000,
  })

  const [draft, setDraft] = useState<RecoveryCouponConfig>(DEFAULTS)
  const [expanded, setExpanded] = useState(false)
  const [newReason, setNewReason] = useState('')
  const messageRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const raw = settingsQuery.data?.data?.[SETTINGS_KEY]
    const parsed = parseConfig(raw)
    setDraft(parsed)
    // Collapsed by default; the header shows a one-line summary so the row
    // stays compact in the "Automatic coupons" list until edited.
  }, [settingsQuery.data])

  const saveMutation = useMutation({
    mutationFn: async (next: RecoveryCouponConfig) => {
      const clamped: RecoveryCouponConfig = {
        enabled: next.enabled,
        tiers: next.tiers
          .filter((t) => t.key.trim() !== '' && t.percent > 0)
          .map((t) => ({
            key: t.key.trim(),
            label: t.label.trim() || t.key.trim(),
            percent: Math.max(1, Math.min(100, t.percent)),
            validity_days: Math.max(1, Math.min(365, Math.round(t.validity_days))),
          })),
        override_cap: Math.max(1, Math.min(100, next.override_cap)),
        reasons: next.reasons.map((r) => r.trim()).filter((r) => r !== ''),
        receipt_message: next.receipt_message.trim() === '' ? DEFAULT_MESSAGE : next.receipt_message,
      }
      await apiClient.updateSetting(SETTINGS_KEY, clamped)
      return clamped
    },
    onSuccess: (_data, variables) => {
      toast({
        title: variables.enabled ? 'Recovery coupon enabled' : 'Recovery coupon saved',
        description: variables.enabled
          ? 'Managers can issue a comeback code from the counter with a PIN.'
          : undefined,
      })
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: 'Could not save', description: msg, variant: 'destructive' })
    },
  })

  const loading = settingsQuery.isLoading

  const persisted = useMemo(
    () => parseConfig(settingsQuery.data?.data?.[SETTINGS_KEY]),
    [settingsQuery.data],
  )
  const dirty = useMemo(
    () => JSON.stringify(persisted) !== JSON.stringify(draft),
    [persisted, draft],
  )

  // One-line summary shown in the collapsed header so the row is
  // self-describing inside the "Automatic coupons" list.
  const summary = useMemo(() => {
    const n = draft.tiers.length
    return `${n} severity tier${n === 1 ? '' : 's'} · up to ${draft.override_cap}% override`
  }, [draft.tiers.length, draft.override_cap])

  // Live receipt preview. Recovery coupons are issued per-tier at the
  // counter but the copy is shared, so the preview uses the first tier as
  // a representative example for the {{discount}} + expiry substitution.
  const previewTier = draft.tiers[0]
  const previewPercent = previewTier?.percent ?? 30
  const previewValidity = previewTier?.validity_days ?? 30
  const previewMessage = useMemo(() => {
    const discount = `${String(Number(previewPercent.toFixed(2)))}%`
    return draft.receipt_message
      .replace(/\{\{code\}\}/g, 'A7F2X9')
      .replace(/\{\{discount\}\}/g, discount)
  }, [draft.receipt_message, previewPercent])
  const previewExpiry = useMemo(
    () =>
      new Date(Date.now() + previewValidity * 86400_000).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }),
    [previewValidity],
  )

  const updateTier = (i: number, patch: Partial<RecoveryCouponTier>) =>
    setDraft((d) => ({
      ...d,
      tiers: d.tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)),
    }))

  const addReason = () => {
    const r = newReason.trim()
    if (r === '' || draft.reasons.includes(r)) return
    setDraft((d) => ({ ...d, reasons: [...d.reasons, r] }))
    setNewReason('')
  }

  return (
    <div
      id="recovery-coupon"
      className="rounded-xl border border-border/80 bg-gradient-to-br from-amber-500/[0.04] via-background to-background shadow-sm"
    >
      <div
        className={cn(
          'flex cursor-pointer select-none items-start gap-3 rounded-t-xl px-5 pt-5 transition-colors hover:bg-foreground/[0.015]',
          !expanded && 'rounded-b-xl pb-5',
        )}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((x) => !x)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setExpanded((x) => !x)
          }
        }}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
          <LifeBuoy className="h-4.5 w-4.5" aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">Recovery coupon</h3>
            {draft.enabled ? (
              <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-sm font-semibold uppercase tracking-wider text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                On
              </span>
            ) : (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Off
              </span>
            )}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {expanded
              ? 'When a guest has a bad moment, a manager can issue a one-off coupon for their next visit, authorised with a manager PIN.'
              : summary}
          </p>
        </div>
        <div
          className="flex shrink-0 items-center gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <Switch
            id="recovery-coupon-enabled"
            checked={draft.enabled}
            onCheckedChange={(v) => {
              setDraft((d) => ({ ...d, enabled: v }))
              if (v) setExpanded(true)
            }}
            disabled={loading}
            aria-label="Enable recovery coupon"
          />
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="space-y-5 px-5 pb-5 pt-5">
          <div className="h-px bg-border/60" />

          {/* Tiers */}
          <div className="space-y-3">
            <Label className="text-sm">Severity tiers</Label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {draft.tiers.map((t, i) => (
                <div
                  key={i}
                  className="relative space-y-2 rounded-lg border border-border/60 bg-background/60 p-3"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setDraft((d) => ({ ...d, tiers: d.tiers.filter((_, idx) => idx !== i) }))
                    }
                    className="absolute right-1.5 top-1.5 rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Remove tier"
                    disabled={draft.tiers.length <= 1}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                  <div className="space-y-1 pr-7">
                    <Label htmlFor={`tier-label-${i}`} className="text-sm text-muted-foreground">
                      Label
                    </Label>
                    <Input
                      id={`tier-label-${i}`}
                      value={t.label}
                      onChange={(e) => updateTier(i, { label: e.target.value })}
                      placeholder="e.g. Major"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label htmlFor={`tier-pct-${i}`} className="text-sm text-muted-foreground">
                        Percent off
                      </Label>
                      <Input
                        id={`tier-pct-${i}`}
                        type="number"
                        inputMode="decimal"
                        min={1}
                        max={100}
                        value={t.percent}
                        onChange={(e) =>
                          updateTier(i, {
                            percent: Number.isFinite(Number(e.target.value))
                              ? Number(e.target.value)
                              : t.percent,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`tier-val-${i}`} className="text-sm text-muted-foreground">
                        Valid (days)
                      </Label>
                      <Input
                        id={`tier-val-${i}`}
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={365}
                        value={t.validity_days}
                        onChange={(e) =>
                          updateTier(i, {
                            validity_days: Number.isFinite(Number(e.target.value))
                              ? Number(e.target.value)
                              : t.validity_days,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-sm"
              onClick={() =>
                setDraft((d) => ({
                  ...d,
                  tiers: [
                    ...d.tiers,
                    { key: `tier_${d.tiers.length + 1}`, label: '', percent: 20, validity_days: 30 },
                  ],
                }))
              }
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add tier
            </Button>
          </div>

          {/* Override cap + reasons — paired side by side to keep the panel
              compact instead of two full-width rows. */}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {/* Override cap */}
            <div className="space-y-1.5">
              <Label htmlFor="recovery-override-cap" className="text-sm">
                Max manager override (%)
              </Label>
              <Input
                id="recovery-override-cap"
                type="number"
                inputMode="decimal"
                min={1}
                max={100}
                value={draft.override_cap}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    override_cap: Number.isFinite(Number(e.target.value))
                      ? Number(e.target.value)
                      : d.override_cap,
                  }))
                }
              />
              <p className="text-sm text-muted-foreground">
                Hard ceiling on the free-form discount a manager can type in the moment.
              </p>
            </div>

            {/* Reasons */}
            <div className="space-y-2">
              <Label className="text-sm">Reasons (required at issue time)</Label>
              <div className="flex flex-wrap gap-1.5">
                {draft.reasons.map((r) => (
                  <span
                    key={r}
                    className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-background px-2.5 py-1 text-sm text-foreground"
                  >
                    {r}
                    <button
                      type="button"
                      onClick={() =>
                        setDraft((d) => ({ ...d, reasons: d.reasons.filter((x) => x !== r) }))
                      }
                      className="rounded-full text-muted-foreground hover:text-destructive"
                      aria-label={`Remove ${r}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addReason()
                    }
                  }}
                  placeholder="Add a reason…"
                  className="h-8 max-w-xs text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-sm"
                  onClick={addReason}
                  disabled={newReason.trim() === ''}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>

          {/* Receipt copy — printed alongside the code when this coupon
              takes the comeback coupon's place on the slip. */}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-[1.05fr_1fr]">
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <Label htmlFor="recovery-message" className="text-sm">
                  Receipt copy
                </Label>
                {draft.receipt_message !== DEFAULT_MESSAGE && (
                  <button
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, receipt_message: DEFAULT_MESSAGE }))}
                    className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                  >
                    Reset to default
                  </button>
                )}
              </div>
              <Textarea
                ref={messageRef}
                id="recovery-message"
                value={draft.receipt_message}
                onChange={(e) => setDraft((d) => ({ ...d, receipt_message: e.target.value }))}
                rows={3}
                placeholder={DEFAULT_MESSAGE}
              />
              <TokenChips
                tokens={[{ label: 'Discount', token: '{{discount}}' }]}
                onInsert={(token) =>
                  insertAtCursor(messageRef.current, token, draft.receipt_message, (v) =>
                    setDraft((d) => ({ ...d, receipt_message: v })),
                  )
                }
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider text-muted-foreground">
                Receipt preview
              </Label>
              <ReceiptCouponPreview
                headline="OUR TREAT NEXT TIME"
                message={previewMessage}
                code="A7F2X9"
                expiry={previewExpiry}
              />
              <p className="text-sm text-muted-foreground">
                Prints in place of the comeback coupon when a manager issues a recovery code.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-border/60 pt-4">
            <Button
              onClick={() => saveMutation.mutate(draft)}
              disabled={saveMutation.isPending || loading || !dirty}
              size="sm"
            >
              {saveMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {dirty ? 'Save changes' : 'Saved'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
